import formatAnswer from "../form/formatAnswer";
import { getQuestionnaireMaxScore } from "./questionnaireScores";

function translateWithFallback(
  questionnaireTranslate,
  commonTranslate,
  key,
  options,
) {
  if (typeof key !== "string" || !key.trim()) {
    return "";
  }

  // Shared labels must not be looked up in a questionnaire namespace first:
  // i18next logs that expected miss even when the common fallback succeeds.
  if (key.startsWith("common.")) {
    const commonValue =
      typeof commonTranslate === "function" ? commonTranslate(key, options) : key;

    return commonValue && commonValue !== key ? commonValue : "N/A";
  }

  const questionnaireValue =
    typeof questionnaireTranslate === "function"
      ? questionnaireTranslate(key, options)
      : key;

  if (questionnaireValue && questionnaireValue !== key) {
    return questionnaireValue;
  }

  const commonValue =
    typeof commonTranslate === "function" ? commonTranslate(key, options) : key;

  if (commonValue && commonValue !== key) {
    return commonValue;
  }

  return key;
}

export function resolveQuestionnaireMetadataText(
  metadata,
  keyField,
  fallbackField,
  questionnaireTranslate,
  commonTranslate,
) {
  if (!metadata || typeof metadata !== "object") {
    return null;
  }

  const translationKey = metadata[keyField];

  if (typeof translationKey === "string" && translationKey.trim()) {
    const translatedText = translateWithFallback(
      questionnaireTranslate,
      commonTranslate,
      translationKey,
    );

    if (translatedText && translatedText !== translationKey) {
      return translatedText;
    }
  }

  const fallbackText = metadata[fallbackField];
  return typeof fallbackText === "string" && fallbackText.trim()
    ? fallbackText
    : null;
}

function resolveQuestionLabel(question, questionnaireTranslate, commonTranslate) {
  if (question?.titleKey) {
    return translateWithFallback(
      questionnaireTranslate,
      commonTranslate,
      question.titleKey,
    );
  }

  if (typeof question?.title === "string" && question.title.trim()) {
    return question.title;
  }

  if (typeof question?.id === "string" && question.id.trim()) {
    return question.id;
  }

  return "Question";
}

export function buildQuestionnaireReviewSnapshot({
  questionnaireId,
  questionnaireMetadata = null,
  questions = [],
  formData = {},
  questionnaireTranslate = () => "",
  commonTranslate = () => "",
}) {
  const questionnaireTitle =
    resolveQuestionnaireMetadataText(
      questionnaireMetadata,
      "titleKey",
      "title",
      questionnaireTranslate,
      commonTranslate,
    ) || questionnaireId;
  const questionnaireShortLabel =
    resolveQuestionnaireMetadataText(
      questionnaireMetadata,
      "shortLabelKey",
      "shortLabel",
      questionnaireTranslate,
      commonTranslate,
    ) || null;
  const translate = (key, options) =>
    translateWithFallback(
      questionnaireTranslate,
      commonTranslate,
      key,
      options,
    );

  return {
    questionnaireTitle,
    questionnaireShortLabel,
    maxScore: getQuestionnaireMaxScore(questionnaireId),
    items: (Array.isArray(questions) ? questions : []).map((question) => ({
      id: question.id,
      fieldType: question.fieldType || null,
      label: resolveQuestionLabel(
        question,
        questionnaireTranslate,
        commonTranslate,
      ),
      answerText: formatAnswer(question, formData, translate),
    })),
  };
}
